import React, { useEffect, useState } from "react";
import { api, resolveImageUrl, formatApiErrorDetail } from "../../lib/api";
import { Handshake, Trash2, Pencil, Plus, Upload, X } from "lucide-react";

const KATEGORIEN = ["Wallbox", "Versicherung", "Smart Home", "Wärmepumpe", "Speicher", "Finanzierung", "Sonstiges"];

export default function KooperationspartnerAdmin() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(blank());
  const [error, setError] = useState("");
  const [uploadBusy, setUploadBusy] = useState(false);

  const load = () => api.get("/admin/kooperationspartner").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const save = async () => {
    setError("");
    try {
      if (editing) await api.put(`/admin/kooperationspartner/${editing.id}`, form);
      else await api.post("/admin/kooperationspartner", form);
      setEditing(null); setForm(blank()); load();
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Partner wirklich löschen?")) return;
    await api.delete(`/admin/kooperationspartner/${id}`);
    load();
  };

  const edit = (p) => { setEditing(p); setForm({ ...p }); };

  const uploadLogo = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    setUploadBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const r = await api.post("/admin/kooperationspartner/logo-upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setForm((s) => ({ ...s, logo_url: r.data.path }));
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Logo-Upload fehlgeschlagen");
    } finally { setUploadBusy(false); }
  };

  return (
    <div className="space-y-5" data-testid="partner-admin-page">
      <h1 className="text-3xl font-heading font-bold text-falke-blue inline-flex items-center gap-2"><Handshake /> Kooperationspartner</h1>

      <div className="falke-card p-6 space-y-3">
        <h3 className="font-heading text-lg text-falke-blue inline-flex items-center gap-2">
          {editing ? <><Pencil size={16} /> Bearbeite: {editing.name}</> : <><Plus size={16} /> Neuer Partner</>}
        </h3>
        <div className="grid sm:grid-cols-2 gap-3">
          <input placeholder="Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="px-3 py-2 rounded-xl border border-falke-blue/15" data-testid="kp-name" />
          <select value={form.kategorie} onChange={(e) => setForm({ ...form, kategorie: e.target.value })} className="px-3 py-2 rounded-xl border border-falke-blue/15" data-testid="kp-kat">
            {KATEGORIEN.map((k) => <option key={k}>{k}</option>)}
          </select>
          <textarea placeholder="Beschreibung" value={form.beschreibung} onChange={(e) => setForm({ ...form, beschreibung: e.target.value })} className="sm:col-span-2 px-3 py-2 rounded-xl border border-falke-blue/15" rows={2} data-testid="kp-desc" />
          <input placeholder="Website (https://…)" value={form.website || ""} onChange={(e) => setForm({ ...form, website: e.target.value })} className="px-3 py-2 rounded-xl border border-falke-blue/15" data-testid="kp-web" />
          <input placeholder="Telefon" value={form.telefon || ""} onChange={(e) => setForm({ ...form, telefon: e.target.value })} className="px-3 py-2 rounded-xl border border-falke-blue/15" data-testid="kp-tel" />
          <input placeholder="E-Mail" value={form.email || ""} onChange={(e) => setForm({ ...form, email: e.target.value })} className="px-3 py-2 rounded-xl border border-falke-blue/15" data-testid="kp-email" />
          <input placeholder="Sortierung (0=oben)" type="number" value={form.sortierung} onChange={(e) => setForm({ ...form, sortierung: Number(e.target.value) })} className="px-3 py-2 rounded-xl border border-falke-blue/15" data-testid="kp-sort" />
          <input placeholder='Rabatt-Code (z.B. "FALKETEK10")' value={form.rabatt_code || ""} onChange={(e) => setForm({ ...form, rabatt_code: e.target.value })} className="px-3 py-2 rounded-xl border border-falke-blue/15" data-testid="kp-code" />
          <input placeholder='Rabatt-Text (z.B. "10 % auf Installation")' value={form.rabatt_text || ""} onChange={(e) => setForm({ ...form, rabatt_text: e.target.value })} className="px-3 py-2 rounded-xl border border-falke-blue/15" data-testid="kp-rab" />

          <div className="sm:col-span-2 flex items-center gap-3">
            <div className="w-16 h-16 rounded-xl border-2 border-dashed border-falke-blue/20 bg-falke-warmwhite flex items-center justify-center overflow-hidden flex-shrink-0">
              {form.logo_url ? <img src={resolveImageUrl(form.logo_url)} alt="" className="w-full h-full object-contain" /> : <Handshake className="text-falke-blue/30" size={22} />}
            </div>
            <label className="btn-outline text-sm inline-flex items-center gap-2 cursor-pointer">
              <Upload size={14} /> {form.logo_url ? "Logo ändern" : "Logo hochladen"}
              <input type="file" accept="image/png,image/jpeg,image/webp,image/svg+xml" onChange={uploadLogo} className="hidden" data-testid="kp-logo-input" />
            </label>
            {form.logo_url && (
              <button type="button" onClick={() => setForm((s) => ({ ...s, logo_url: null }))} className="text-rose-600 inline-flex items-center gap-1 text-sm" data-testid="kp-logo-remove"><X size={14} /> Entfernen</button>
            )}
            {uploadBusy && <span className="text-xs text-falke-blue/60">Lade…</span>}
          </div>

          <label className="flex items-center gap-2 sm:col-span-2"><input type="checkbox" checked={form.aktiv} onChange={(e) => setForm({ ...form, aktiv: e.target.checked })} className="w-4 h-4 accent-falke-orange" /> Aktiv (für Kunden sichtbar)</label>
        </div>
        {error && <div className="text-sm text-rose-600" data-testid="kp-error">{error}</div>}
        <div className="flex gap-2">
          <button onClick={save} className="btn-primary" data-testid="kp-save">{editing ? "Aktualisieren" : "Anlegen"}</button>
          {editing && <button onClick={() => { setEditing(null); setForm(blank()); }} className="btn-outline">Abbrechen</button>}
        </div>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.map((p) => (
          <div key={p.id} className="falke-card p-4" data-testid={`kp-card-${p.id}`}>
            <div className="flex items-center gap-3 mb-2">
              {p.logo_url ? <img src={resolveImageUrl(p.logo_url)} alt="" className="w-10 h-10 rounded object-contain" /> : <div className="w-10 h-10 rounded bg-falke-blue/10 flex items-center justify-center text-falke-blue font-bold">{p.name[0]}</div>}
              <div className="min-w-0 flex-1">
                <div className="font-semibold text-falke-blue truncate">{p.name}</div>
                <div className="text-[10px] text-falke-anthracite/60">{p.kategorie} · {p.aktiv ? "aktiv" : "inaktiv"}</div>
              </div>
            </div>
            <div className="text-xs text-falke-anthracite/70 line-clamp-2 mb-2">{p.beschreibung}</div>
            {p.rabatt_text && <div className="text-xs text-falke-orange font-semibold mb-2">{p.rabatt_text} {p.rabatt_code && `· ${p.rabatt_code}`}</div>}
            <div className="flex gap-2">
              <button onClick={() => edit(p)} className="btn-outline !py-1.5 text-xs flex-1" data-testid={`kp-edit-${p.id}`}>Bearbeiten</button>
              <button onClick={() => remove(p.id)} className="text-rose-600 inline-flex items-center gap-1 text-xs" data-testid={`kp-del-${p.id}`}><Trash2 size={12} /> Löschen</button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function blank() {
  return { name: "", kategorie: "Wallbox", beschreibung: "", logo_url: null, website: "", telefon: "", email: "", rabatt_code: "", rabatt_text: "", aktiv: true, sortierung: 0 };
}
